
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, Instagram, Gift } from 'lucide-react';
import { cn } from '@/lib/utils';
import ReserveButton from '../ReserveButton';

interface MobileMenuProps {
  isMenuOpen: boolean;
  menuHeight: string;
  navbarRef: React.RefObject<HTMLElement>;
  closeMenu: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  isMenuOpen,
  menuHeight,
  navbarRef,
  closeMenu
}) => {
  const location = useLocation();

  const navLinks = [
    { name: 'About', path: '/about' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Blog', path: '/blog' },
    { name: 'In the News', path: '/in-the-news' },
    { name: 'Contact', path: '/contact' },
  ];

  const tourLinks = [
    { name: 'Brooklyn Heights', path: '/tours/brooklyn-heights' },
    { name: 'Financial District', path: '/tours/financial-district' },
    { name: 'Lower East Side', path: '/tours/lower-east-side' },
    { name: 'Private Tours', path: '/tours/private-tours' },
  ];

  return (
    <div 
      id="mobile-menu"
      className={cn(
        'fixed inset-0 bg-[#F5F5DC]/95 supports-backdrop-blur:backdrop-blur-md z-40 transition-all duration-500 ease-in-out md:hidden overflow-auto',
        isMenuOpen ? 'translate-x-0' : '-translate-x-full'
      )}
      style={{ top: navbarRef.current ? navbarRef.current.offsetHeight : '70px', maxHeight: menuHeight }}
      aria-hidden={!isMenuOpen}
      role="dialog"
      aria-modal="true"
      aria-label="Mobile navigation menu"
    >
      <nav className="py-4 px-6 flex flex-col">
        <ul className="space-y-1">
          {navLinks.map(link => (
            <li key={link.path}>
              <Link 
                to={link.path}
                className={cn(
                  'py-3 px-4 border-b border-[#8D6E63]/10 text-lg font-montserrat font-medium transition-all duration-300 block',
                  location.pathname === link.path 
                    ? 'text-[#657C56] font-semibold' 
                    : 'text-[#3E2723] hover:text-[#657C56]'
                )}
                onClick={closeMenu}
                aria-current={location.pathname === link.path ? 'page' : undefined}
              >
                {link.name}
              </Link>
            </li>
          ))}
          
          {/* Mobile Tours Section */}
          <li>
            <div className="py-3 px-4 border-b border-[#8D6E63]/10 text-lg font-montserrat font-medium text-[#3E2723]">
              Tours
            </div>
            <ul className="ml-4 space-y-1">
              {tourLinks.map(tour => (
                <li key={tour.path}>
                  <Link 
                    to={tour.path}
                    className={cn(
                      'py-2 px-4 text-base font-montserrat transition-all duration-300 block',
                      location.pathname === tour.path 
                        ? 'text-[#657C56] font-semibold' 
                        : 'text-[#3E2723] hover:text-[#657C56]'
                    )}
                    onClick={closeMenu}
                    aria-current={location.pathname === tour.path ? 'page' : undefined}
                  >
                    {tour.name}
                  </Link>
                </li>
              ))}
              <li>
                <a 
                  href="https://fareharbor.com/embeds/book/catsabouttown/items/562633/calendar/?full-items=yes&flow=1219724" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-2 px-4 text-base font-montserrat transition-all duration-300 block text-[#3E2723] hover:text-[#657C56] flex items-center"
                  onClick={closeMenu}
                >
                  <Gift className="mr-2 h-4 w-4" />
                  Gift Cards
                </a>
              </li>
            </ul>
          </li>
        </ul>
        <ReserveButton 
          className="mt-6 text-center" 
          fullWidth={true}
        />
        
        {/* Social links in mobile menu */}
        <div className="mt-8 flex justify-center gap-4" aria-label="Social Links">
          <a 
            href="mailto:catsabouttowntour@gmail.com"
            className="p-3 bg-[#8D6E63]/10 rounded-full text-[#3E2723] hover:bg-[#8D6E63]/20 transition-all duration-300"
            aria-label="Email us"
          >
            <Mail size={20} aria-hidden="true" />
          </a>
          <a 
            href="https://www.instagram.com/catsabouttowntours/"
            target="_blank"
            rel="noopener noreferrer"
            className="p-3 bg-[#8D6E63]/10 rounded-full text-[#3E2723] hover:bg-[#8D6E63]/20 transition-all duration-300"
            aria-label="Follow us on Instagram"
          >
            <Instagram size={20} aria-hidden="true" />
          </a>
        </div>
      </nav>
    </div>
  );
};

export default MobileMenu;
